import type { FilterValue, NullPolicy, SortState } from '@/components/data/types'

/**
 * The URL grammar for one table's sort and filters.
 *
 * ── WHY THE URL AT ALL ──────────────────────────────────────────────────────
 * `useUrlState` states the contract: "the URL is also the query key", and the
 * main OUTPUT of a staff tool is a link somebody else opens later. A sort and a
 * filter that live in component state make the interesting view of a table
 * unshareable — which on a console whose job is to answer "look at this" is the
 * feature quietly not working.
 *
 * ── AND WHY ONE PACKED PARAM PER TABLE ──────────────────────────────────────
 * One parameter per column would give the organization-detail page a
 * ten-key query string, and the dashboard an eleven-key one on its own.
 * `useUrlState` already argues the other side of
 * this — it deletes any value equal to its default "so the common link stays
 * short and readable in a Slack preview" — and packing is the same argument one
 * level up: in the common case `<tableId>.f` is absent entirely.
 *
 * ── THE GRAMMAR ─────────────────────────────────────────────────────────────
 *   <tableId>.s = <columnId>:asc|desc          absent  = the screen's own order
 *   <tableId>.q = <quickId>,<quickId>
 *   <tableId>.f = <col>~<col>~…
 *       <col>   = <columnId>:<tag>:<payload>[!<policy>]
 *       tag     = t text · m multi · r range · d dates · b bool
 *       policy  = i include unmeasured · o only unmeasured · absent = exclude
 *
 * The policy suffix is legal on EVERY tag, not just the ranged ones. An earlier
 * draft attached it to `r` and `d` only, while the popover offered
 * `Solo sin medir` on every kind — so that view could not be shared and
 * reverted on reload, which is precisely the property this file exists to
 * guarantee.
 */

const COL_SEP = '~'
const POLICY: Record<string, NullPolicy> = { i: 'include', o: 'only' }
const POLICY_TAG: Partial<Record<NullPolicy, string>> = { include: 'i', only: 'o' }

function encodePolicy(p: NullPolicy): string {
  const t = POLICY_TAG[p]
  return t ? `!${t}` : ''
}

/** `encodeURIComponent`, plus the three delimiters this grammar reserves. */
function esc(s: string): string {
  return encodeURIComponent(s).replace(/~/g, '%7E').replace(/!/g, '%21').replace(/,/g, '%2C')
}

function unesc(s: string): string {
  try {
    return decodeURIComponent(s)
  } catch {
    // A hand-edited URL is not a crash. Fall back to the raw text.
    return s
  }
}

export function encodeSort(s: SortState): string | null {
  return s.kind === 'default' ? null : `${s.columnId}:${s.direction}`
}

export function decodeSort(raw: string | null): SortState {
  if (!raw) return { kind: 'default' }
  const i = raw.lastIndexOf(':')
  if (i <= 0) return { kind: 'default' }
  const columnId = raw.slice(0, i)
  const direction = raw.slice(i + 1)
  if (direction !== 'asc' && direction !== 'desc') return { kind: 'default' }
  return { kind: 'column', columnId, direction }
}

export function encodeFilters(filters: Record<string, FilterValue>): string | null {
  const parts: string[] = []
  for (const [id, f] of Object.entries(filters)) {
    const p = encodePolicy(f.nulls)
    switch (f.kind) {
      case 'text':
        parts.push(`${id}:t:${esc(f.q)}${p}`)
        break
      case 'multi':
        parts.push(`${id}:m:${f.picked.map(esc).join(',')}${p}`)
        break
      case 'range':
        parts.push(`${id}:r:${f.min ?? ''}..${f.max ?? ''}${p}`)
        break
      case 'dates':
        parts.push(`${id}:d:${f.from ?? ''}..${f.to ?? ''}${p}`)
        break
      case 'bool':
        parts.push(`${id}:b:${f.want ? '1' : '0'}${p}`)
        break
    }
  }
  return parts.length ? parts.join(COL_SEP) : null
}

export function decodeFilters(raw: string | null): Record<string, FilterValue> {
  if (!raw) return {}
  const out: Record<string, FilterValue> = {}
  for (const part of raw.split(COL_SEP)) {
    // The id may not contain `:`, the tag is one character, the rest is payload.
    const m = /^([^:]+):([tmrdb]):(.*)$/.exec(part)
    if (!m) continue
    const [, id, tag, rest] = m
    // The policy suffix, if any. Split from the RIGHT so a `!` inside an
    // escaped payload cannot be mistaken for it (`esc` percent-encodes `!`).
    const bang = rest.lastIndexOf('!')
    const hasPolicy = bang !== -1 && POLICY[rest.slice(bang + 1)] !== undefined
    const payload = hasPolicy ? rest.slice(0, bang) : rest
    const nulls: NullPolicy = hasPolicy ? POLICY[rest.slice(bang + 1)] : 'exclude'

    switch (tag) {
      case 't':
        out[id] = { kind: 'text', q: unesc(payload), nulls }
        break
      case 'm':
        out[id] = {
          kind: 'multi',
          picked: payload === '' ? [] : payload.split(',').map(unesc),
          nulls,
        }
        break
      case 'r': {
        const [lo, hi] = payload.split('..')
        out[id] = {
          kind: 'range',
          min: lo === '' || lo === undefined ? null : Number(lo),
          max: hi === '' || hi === undefined ? null : Number(hi),
          nulls,
        }
        break
      }
      case 'd': {
        const [from, to] = payload.split('..')
        out[id] = {
          kind: 'dates',
          from: from === '' || from === undefined ? null : from,
          to: to === '' || to === undefined ? null : to,
          nulls,
        }
        break
      }
      case 'b':
        out[id] = { kind: 'bool', want: payload === '1', nulls }
        break
    }
  }
  return out
}

export function encodeQuick(ids: string[]): string | null {
  return ids.length ? ids.join(',') : null
}

export function decodeQuick(raw: string | null): string[] {
  return raw ? raw.split(',').filter(Boolean) : []
}
